import type { AutopilotSettings, TiktokPostOptions, ProjectProfile, BriefingCheckItem } from '@aff/shared';
import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from './api.js';

/**
 * Alle Serverzugriffe an einem Ort. Die Schlüssel sind hierarchisch, damit
 * ein Projektwechsel gezielt genau das ungültig macht, was dazugehört.
 */

export const keys = {
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  posts: (filter: Record<string, unknown>) => ['posts', filter] as const,
  post: (id: string) => ['post', id] as const,
  approvals: ['approvals'] as const,
  run: (projectId: string) => ['run', projectId] as const,
  runs: (projectId: string) => ['runs', projectId] as const,
  connectors: ['connectors'] as const,
  catalog: ['connector-catalog'] as const,
  accounts: (projectId?: string) => ['accounts', projectId ?? 'all'] as const,
  music: (filter: Record<string, unknown>) => ['music', filter] as const,
  billing: ['billing'] as const,
  billingCatalog: ['billing-catalog'] as const,
  margin: (days: number) => ['margin', days] as const,
  system: ['system-status'] as const,
  platform: ['platform-status'] as const,
  platformApps: ['platform-apps'] as const,
  conversations: (projectId?: string | null) => ['conversations', projectId ?? 'all'] as const,
  customers: ['customers'] as const,
  tiktokCreatorInfo: (projectId: string) => ['tiktok-creator-info', projectId] as const,
  agents: (projectId: string) => ['agents', projectId] as const,
  agentPosts: (agentId: string) => ['agent-posts', agentId] as const,
};

/* ---------------------------------------------------------------- Typen */

export interface ProjectSummary {
  id: string;
  name: string;
  type: 'app' | 'product' | 'company';
  websiteUrl: string;
  business: { industry: string; services: string; location: string; audience: string };
  product: { description: string; url: string; affiliate: boolean; affiliateDisclosure: string; audience: string };
  subtitle: string;
  briefing: string;
  postIdeas: string;
  documents: { name: string; text: string }[];
  storeUrl: string;
  appStoreUrl: string;
  playStoreUrl: string;
  goals: string[];
  ctaText: string;
  aiLabel?: 'auto' | 'always' | 'off';
  /** Antworten aus dem Briefing-Interview */
  profile?: ProjectProfile;
  /** Wie gut die AI das Projekt kennt, vom Server berechnet */
  briefingCheck?: { score: number; level: 'thin' | 'ok' | 'strong'; items: BriefingCheckItem[] };
  visualWorld?: string;
  /** Gerechnete Auswertung je Hebel (Format, Element, Hook …) */
  performance?: {
    updatedAt: string; posts: number; medianViewsPerDay: number; medianEngagement: number;
    groups: { dimension: 'format' | 'element' | 'kind' | 'slides' | 'hook' | 'hour'; key: string; n: number; viewsPerDay: number; engagement: number; lift: number }[];
  } | null;
  designSlides?: boolean;
  cardTheme?: string;
  colorGrade?: string;
  slideLayout?: 'framed' | 'mixed' | 'full';
  bioPage?: { enabled: boolean; slug: string; url: string };
  formats?: { name: string; idea: string }[];
  products?: CatalogProduct[];
  tonality: string;
  /** Sprache der Posts, leer = die des Briefings */
  language: string;
  format: string;
  imageStyle: string;
  customStylePrompt: string;
  accentColor: string;
  coverAssetId: string | null;
  coverUrl: string | null;
  schedule: { frequencyIndex: number; windows: boolean[]; timezone: string; label: string; windowLabels: string[]; autoWindows?: boolean };
  draftSchedule: { frequencyIndex: number; windows: boolean[] } | null;
  /*
   * Aus @aff/shared statt hier nochmal getippt. Die Handkopie lief den
   * Serverfeldern staendig hinterher: Jedes neue Feld fiel erst beim
   * Typecheck auf, nachdem die Oberflaeche schon dagegen gebaut war.
   */
  autopilot: AutopilotSettings;
  backlog: string[];
  learnings: string[];
  learningsBasis?: { posts: number; decisions: number };
  learningsUpdatedAt?: string | null;
  /** Feste Vorgaben des Nutzers an den Agenten */
  notes?: string[];
  /** true, solange die AI im Hintergrund noch aus dem Briefing ableitet */
  setupPending?: boolean;
  /** Screenshots der App, Vorlage für Bilder, die die App zeigen */
  references?: { id: string; url: string; label?: string; role?: string }[];
  referenceAssetIds: string[];
  appShare: number;
  nextRunAt: string | null;
  /** Gesetzt, wenn der Autopilot an ist, aber nichts baut — mit Grund. */
  autopilotHold?: { reason: string; since: string | null } | null;
  lastRunAt: string | null;
  stats?: {
    queueCount: number; pendingApproval: number; publishedCount: number;
    views30d: number; likes30d: number; followers: number; creditsSpent30d: number;
  };
  accounts?: SocialAccountView[];
  health?: { key: string; text: string; state: 'ok' | 'warn' | 'error' | 'pending' }[];
  lastRun?: { id: string; status: string; progress: number } | null;
  /** Gesetzt, solange fuer dieses Projekt ein Lauf arbeitet */
  activeRun?: { id: string; status: string; progress: number } | null;
}

export interface SocialAccountView {
  id: string;
  projectId: string;
  platform: 'tiktok' | 'instagram';
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  connected: boolean;
  tokenHealth: 'ok' | 'expiring' | 'expired' | 'missing';
  tokenDaysLeft: number | null;
  sandboxMode: boolean;
  scopes: string[];
  /** Instagram: die Facebook-Seite dahinter und ob dort gepostet werden darf. */
  facebookPage?: { name: string; canPublish: boolean } | null;
  metrics: { followers: number; views30d: number; likes30d: number; posts: number };
  lastSyncAt: string | null;
  lastError: string | null;
}

export interface TextLayerView {
  id: string; text: string; size: 'S' | 'M' | 'L' | 'XL';
  x: number; y: number; color: string; align: string; highlight: boolean;
  /** caption, chat, notification, headline, note */
  style?: string | null;
  sender?: string | null;
}

export interface SceneView {
  purpose?: string;
  visualReason?: string;
  quality?: import('@aff/shared').QualityCheck;
  id: string; kind: 'image' | 'video'; assetId: string | null; url: string | null;
  prompt: string; duration: number; texts: TextLayerView[];
  /** full = Text über dem Motiv, top/bottom = Rahmen-Layout mit Textband */
  layout?: 'full' | 'top' | 'bottom';
  generation: { provider: string; model: string; costMicroUsd: number } | null;
}

export interface PostView {
  agentRunId?: string | null;
  /** Bauplan, nach dem der Beitrag geplant wurde (BLUEPRINTS) */
  format?: string;
  story?: string;
  rationale?: string;
  exportQuality?: import('@aff/shared').QualityCheck;
  textQuality?: import('@aff/shared').QualityCheck;
  costMicroUsd?: number;
  /** Aus dem kostenlosen Probelauf: Platzhalter, nicht veröffentlichbar */
  demo?: boolean;
  /** Nummer auf der „Link in Bio"-Seite und Klicks darüber */
  bioNo?: number | null;
  linkClicks?: number;
  /** Look der Info-Karten (glass, light, brand, paper, neon) */
  overlayTheme?: string;
  productName?: string;
  id: string; projectId: string; status: string; hook: string; caption: string;
  hashtags: string[]; scenes: SceneView[]; trackId: string | null; trackName?: string | null;
  targets: string[]; scheduledAt: string | null; publishedAt: string | null;
  /** Direct-Post-Auswahl des Kontoinhabers, null solange nichts gewaehlt ist */
  tiktokOptions: TiktokPostOptions | null;
  renderedUrl: string | null; thumbnailUrl: string | null;
  photoUrls: (string | null)[]; feedPhotoUrls: (string | null)[];
  creditsSpent: number;
  costBreakdown: { key: string; label: string; qty: number; unit: string; credits: number; costMicroUsd?: number }[];
  metrics: { views: number; likes: number; comments: number; shares: number; saves: number; profileClicks: number; updatedAt?: string | null };
  results: { target: string; state: string; permalink: string | null; error: string | null }[];
  createdBy: string; error: string | null; createdAt: string;
  /** Welches Rezept den Post gebaut hat -- die erste Frage vor jeder Freigabe */
  agentId?: string | null;
  agentName?: string | null;
  /** Erkennungsfarbe des Agenten, leer/null = keine vergeben */
  agentColor?: string | null;
  trackArtist?: string | null;
  /** Serien-Zugehörigkeit: „Teil 2 von 4 der Serie X". Leer = Einzelpost. */
  seriesTitle?: string;
  seriesIndex?: number | null;
  seriesLength?: number | null;
}

export interface AgentRunView {
  recipeSnapshot?: Partial<AgentView> | null;
  awaitingStoryReview?: boolean;
  storyRevision?: string;
  id: string; projectId: string; trigger: string; status: string; dryRun?: boolean;
  /** Welches Rezept läuft, null bei alten Läufen ohne Agent */
  agentId?: string | null;
  /** Gesetzt, wenn die Posts dieses Laufs zusammen eine Geschichte erzählen */
  storyArc?: { mode: string; title: string; premise: string; beats: string[] } | null;
  /** Abbruch ist angefordert, der Lauf hört nach dem laufenden Schritt auf */
  abortRequested?: boolean;
  targetCount: number; currentJobIndex: number; progress: number;
  /** Laufende Nummer im Projekt („Lauf #14") */
  runNumber?: number;
  /** Phase des Laufs, vom Backend gemeldet (nicht aus dem Prozentwert geraten) */
  phase?: RunPhase;
  jobs: {
    storyDraft?: import('@aff/shared').StoryBoardDraft | null;
    postId: string | null; title: string; when: string; kind: string; state: string;
    steps: { key: string; title: string; detail: string; state: string; progress: number }[];
  }[];
  log: { ts: string; level: string; text: string }[];
  creditsReserved: number; creditsSpent: number;
  costBreakdown?: { credits: number }[];
  startedAt: string | null; finishedAt: string | null; error: string | null; createdAt: string;
}

export interface TrackView {
  id: string; title: string; artist: string; genre: string; bpm: number;
  durationSec: number; license: string; attribution: string; mood: string;
  url: string | null; waveform: number[]; global: boolean;
  sourceUrl?: string | null;
}

export interface CatalogTrackView {
  externalId: string; title: string; creator: string; license: string;
  licenseVersion: string; licenseUrl: string; attribution: string;
  audioUrl: string | null; landingUrl: string; provider: string;
  durationSec: number; genres: string[]; requiresAttribution: boolean;
  imported: boolean;
}

export interface ConnectorView {
  id: string; provider: string; label: string; kinds: string[]; enabled: boolean;
  keyHint: string | null; hasKey: boolean; managed: boolean; defaultModel: string | null;
  lastCheckOk: boolean | null; lastCheckError: string | null; note: string;
  projectId: string | null;
}

/* -------------------------------------------------------------- Abfragen */

export const useProjects = () =>
  useQuery({
    queryKey: keys.projects,
    queryFn: () => api.get<{ projects: ProjectSummary[] }>('/projects').then((r) => r.projects),
  });

export const useProject = (id: string | undefined) =>
  useQuery({
    queryKey: keys.project(id ?? ''),
    queryFn: () => api.get<{ project: ProjectSummary }>(`/projects/${id}`).then((r) => r.project),
    enabled: !!id,
    /*
     * Ein frisch angelegtes Projekt steht sofort da, waehrend die AI im
     * Hintergrund noch Untertitel, Tonfall und erste Ideen ableitet. Solange
     * das laeuft, kurz nachfragen -- sonst muesste man die Seite neu laden,
     * um zu sehen, dass etwas nachgekommen ist.
     */
    refetchInterval: (q) => (q.state.data?.setupPending ? 3000 : false),
  });

export const usePosts = (filter: { projectId?: string; status?: string; limit?: number; sort?: string; from?: string; to?: string }) =>
  useQuery({
    queryKey: keys.posts(filter),
    queryFn: () => {
      const p = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => v !== undefined && p.set(k, String(v)));
      return api.get<{ posts: PostView[] }>(`/posts?${p}`).then((r) => r.posts);
    },
    // Solange etwas in Bewegung ist, nachfragen: Die Live-Meldungen sind
    // Komfort, verlassen darf sich die Anzeige nicht auf sie.
    refetchInterval: (q) => (q.state.data?.some((post) => IN_MOTION.has(post.status)) ? 8000 : false),
  });

/** Zustände, die sich ohne Zutun des Nutzers weiterbewegen. */
const IN_MOTION = new Set(['generating', 'approved', 'rendering', 'publishing']);

export const usePost = (id: string | null | undefined, options: Partial<UseQueryOptions> = {}) =>
  useQuery({
    queryKey: keys.post(id ?? ''),
    queryFn: () => api.get<{ post: PostView }>(`/posts/${id}`).then((r) => r.post),
    enabled: !!id,
    refetchInterval: (q) => (IN_MOTION.has((q.state.data as PostView | undefined)?.status ?? '') ? 5000 : false),
    ...(options as object),
  });

export const useApprovals = () =>
  useQuery({
    queryKey: keys.approvals,
    queryFn: () => api.get<{
      groups: { projectId: string; name: string; subtitle: string; accentColor: string; count: number; posts: PostView[] }[];
      total: number;
    }>('/posts/approvals'),
  });

export type RunPhase = 'queued' | 'estimate' | 'plan' | 'media' | 'music' | 'store' | 'done';

/** Ein arbeitender Lauf, so knapp wie die Oberfläche ihn braucht. */
export interface ActiveRun {
  id: string;
  agentId: string | null;
  status: string;
  progress: number;
  phase?: RunPhase;
  currentJobIndex: number;
  jobCount: number;
  currentTitle: string;
  abortRequested: boolean;
}

export const useCurrentRun = (projectId: string | undefined, options: Partial<UseQueryOptions> = {}) =>
  useQuery({
    queryKey: keys.run(projectId ?? ''),
    queryFn: () => api.get<{
      run: AgentRunView | null;
      /** ALLE Läufe, die gerade arbeiten — Agenten laufen parallel */
      active: ActiveRun[];
      nextRunAt: string | null;
      backlog: string[];
    }>(`/agent/runs/current?projectId=${projectId}`),
    enabled: !!projectId,
    refetchInterval: (query) => {
      // Takten, solange IRGENDEIN Agent arbeitet, nicht nur der zuletzt
      // gestartete: Sonst friert die Anzeige der anderen ein.
      const data = query.state.data as { active?: ActiveRun[] } | undefined;
      return data?.active?.length ? 2500 : false;
    },
    ...(options as object),
  });

/**
 * Frühere Läufe. Mit `agentId` nur die dieses Rezepts — auf der Seite eines
 * Agenten sind die Läufe der anderen nur Rauschen.
 */
export const useRuns = (projectId: string | undefined, agentId?: string) =>
  useQuery({
    queryKey: [...keys.runs(projectId ?? ''), agentId ?? 'all'] as const,
    queryFn: () => api.get<{ runs: AgentRunView[] }>(
      `/agent/runs?projectId=${projectId}&limit=15${agentId ? `&agentId=${agentId}` : ''}`,
    ).then((r) => r.runs),
    enabled: !!projectId,
  });

/** Ein bestimmter Lauf, für die Ansicht eines früheren Laufs. */
export const useRun = (runId: string | undefined) =>
  useQuery({
    queryKey: ['run-detail', runId ?? ''] as const,
    queryFn: () => api.get<{ run: AgentRunView }>(`/agent/runs/${runId}`).then((r) => r.run),
    enabled: !!runId,
    // Ein laufender Lauf, der über seine Adresse geöffnet wurde, bewegt sich sonst nie.
    refetchInterval: (q) => (['queued', 'running'].includes(q.state.data?.status ?? '') ? 3000 : false),
  });

export const useConnectors = () =>
  useQuery({
    queryKey: keys.connectors,
    queryFn: () => api.get<{ connectors: ConnectorView[] }>('/connectors').then((r) => r.connectors),
  });

/*
 * Preise kommen in der Waehrung, in der das Konto zahlt.
 *
 * Credits sieht jeder -- das ist der Preis beim Kunden. Die Einkaufspreise
 * in USD/EUR liefert der Server NUR an Konten, die selbst beim Anbieter
 * zahlen (Betreiber und Freundeskonten); bei allen anderen fehlen die
 * Felder, weil sich aus ihnen unsere Marge ablesen liesse.
 */
export interface ModelPrice {
  key: string; label: string; provider: string; unit: string; note: string;
  tier: 'basis' | 'stark' | 'premium';
  creditsPerUnit: number;
  priceUnverified: boolean;
  costUsdPerUnit?: number; costEurPerUnit?: number;
}

export interface TextModelPrice {
  key: string; label: string; provider: string; unit: string;
  note: string;
  /** Was ein Post an Text ungefähr kostet, in Credits */
  creditsPerPost: number;
  /** Nutzbar: über unseren Zugang ODER mit eigenem Key */
  available: boolean;
  ownKey: boolean;
  inputUsdPerMTok?: number; outputUsdPerMTok?: number;
}

export interface ConnectorCatalog {
  providers: {
    provider: string; label: string; kinds: string[];
    models: { key: string; label: string; kind: string }[];
    platformKeyAvailable: boolean; needsSecret: boolean;
    /** Wo man sich einen eigenen Key holt */
    keyUrl: string | null;
    /** Diese Organisation hat einen eigenen, funktionierenden Key hinterlegt */
    ownKey: boolean;
    /** Eigener Key hinterlegt, aber vom Anbieter abgelehnt */
    keyRejected: boolean;
    /** Nutzbar: über unseren Zugang ODER mit eigenem Key */
    available: boolean;
  }[];
  models: { image: ModelPrice[]; video: ModelPrice[]; voice: ModelPrice[]; text: TextModelPrice[] };
  unverifiedPrices: { key: string; label: string; provider: string }[];
}

export const useConnectorCatalog = () =>
  useQuery({
    queryKey: keys.catalog,
    queryFn: () => api.get<ConnectorCatalog>('/connectors/catalog'),
    staleTime: 5 * 60_000,
  });

interface ReadinessKind {
  ok: boolean;
  code?: string;
  message?: string;
  provider?: string;
  /** Das Modell, das liefe, wenn am Agenten keins eingestellt ist. */
  model?: string;
  /** Kein echter Zugang: im Testmodus entstehen Platzhalter */
  mocked?: boolean;
}

export interface Readiness {
  ok: boolean;
  text: ReadinessKind;
  image: ReadinessKind;
  video: ReadinessKind;
  /** Ein Satz, der sagt, was fehlt -- null, wenn nichts fehlt. */
  reason: string | null;
}

/**
 * Kann dieses Konto gerade bauen?
 *
 * Wird von jedem „Posts bauen"-Knopf gelesen. Solange die Antwort fehlt,
 * gilt NICHT bereit -- lieber einen Moment grau als ein Klick, der im
 * Worker scheitert.
 */
export const useReadiness = (projectId?: string, enabled = true) =>
  useQuery({
    queryKey: [...keys.catalog, 'readiness', projectId ?? null],
    queryFn: () => api.get<Readiness>(`/connectors/readiness${projectId ? `?projectId=${projectId}` : ''}`),
    staleTime: 30_000,
    enabled,
  });

export const useAccounts = (projectId?: string) =>
  useQuery({
    queryKey: keys.accounts(projectId),
    queryFn: () => api.get<{ accounts: SocialAccountView[] }>(
      `/social/accounts${projectId ? `?projectId=${projectId}` : ''}`,
    ).then((r) => r.accounts),
  });

export const useMusic = (filter: { genre?: string; mood?: string }) =>
  useQuery({
    queryKey: keys.music(filter),
    queryFn: () => {
      const p = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => v && p.set(k, v));
      return api.get<{ tracks: TrackView[]; genres: string[]; moods: string[] }>(`/music?${p}`);
    },
  });

export interface BillingOverview {
  balance: number; reserved: number; available: number;
  /** Konto ohne Guthabengrenze (Freund oder Testkonto) */
  unlimited?: boolean;
  /** Werden Tarife und Pakete angeboten? Fuer Betreiber immer, siehe billing.ts */
  showsCatalog?: boolean;
  spentToday: number; spent30d: number; runwayDays: number | null;
  plan: { key: string; name: string; priceEur: number; monthlyCredits: number; maxProjects: number; features: string[] };
  subscriptionStatus: string;
  autoTopUp: { enabled: boolean; thresholdCredits: number; packSku: string | null };
  dailyCreditCap: number | null;
  lifetime: { purchased: number; spent: number; costEur: number };
  ledger: { id: string; delta: number; balanceAfter: number; reason: string; label: string; costEur: number; createdAt: string }[];
}

export const useBilling = () =>
  useQuery({ queryKey: keys.billing, queryFn: () => api.get<BillingOverview>('/billing/overview') });

export const useBillingCatalog = () =>
  useQuery({
    queryKey: keys.billingCatalog,
    queryFn: () => api.get<{
      packs: { sku: string; name: string; credits: number; bonusCredits: number; totalCredits: number; priceEur: number; pricePerCreditEur: number; popular: boolean; description: string; accent?: string }[];
      plans: {
        key: string; name: string; tagline: string; accent: string; popular: boolean;
        priceEur: number; monthlyCredits: number; maxProjects: number;
        maxAccountsPerProject: number; features: string[];
      }[];
      examples: { label: string; credits: number; approxEur: number }[];
      stripeEnabled: boolean;
      /** Pflichtangabe neben jedem Preis, kommt vom Server (§ 19 UStG o. ä.) */
      taxNote: string;
    }>('/billing/catalog'),
    staleTime: 10 * 60_000,
  });

export const useMargin = (days = 30) =>
  useQuery({
    queryKey: keys.margin(days),
    queryFn: () => api.get<{
      days: number; creditsSold: number; creditsSpent: number;
      costEur: number; revenueEur: number; grossMarginEur: number; grossMarginPct: number;
      byKey: { key: string; credits: number; costEur: number }[];
      perPost: { posts: number; avgCredits: number; avgCostEur: number; avgViews: number } | null;
      config: { margin: number; creditValueUsd: number; worstCaseCreditPriceEur: number };
    }>(`/billing/margin?days=${days}`),
  });

/** Betreiberansicht: Der Server liefert sie auch nur dem Betreiber aus. */
export const useSystemStatus = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: keys.system,
    queryFn: () => api.get<{
      checks?: { key: string; label: string; ok: boolean; required: boolean; detail: string }[];
      ai: { enabled: boolean; model: string };
      storage: { driver: string; ok: boolean; detail: string };
      render: { ffmpeg: boolean; ffmpegVersion: string; fonts: { ok: boolean; bold: string | null } };
      queue: Record<string, number>;
      platforms: { tiktok: { configured: boolean; sandbox: boolean }; instagram: { configured: boolean; graphVersion: string } };
      billing: { stripe: boolean };
      pricing: { margin: number; creditValueUsd: number; unverifiedPrices: { key: string; label: string }[] };
      warnings: string[];
    }>('/status'),
    staleTime: 30_000,
  });

export interface PlatformState {
  platform: 'tiktok' | 'instagram';
  configured: boolean;
  fromEnv: boolean;
  clientId: string;
  secretHint: string;
  enabled: boolean;
  auditPassed: boolean;
  verifiedDomain: string | null;
  editable: boolean;
  note: string;
  redirectUri: string;
  sandbox?: boolean;
  canPullFromUrl?: boolean;
  ready?: boolean;
  graphVersion?: string;
  /** Instagram: Anmeldung direkt bei Instagram oder über Facebook-Seite */
  loginVia?: 'instagram' | 'facebook';
}

export const usePlatformStatus = () =>
  useQuery({
    queryKey: keys.platform,
    /*
     * Kunden bekommen eine schmalere Antwort als der Betreiber: nur, ob die
     * App steht. Client-Ids, Redirect-URIs und CDN-Domain liefert der
     * Server ihnen gar nicht erst -- deshalb sind die Felder optional.
     */
    queryFn: () => api.get<{
      tiktok: Partial<PlatformState> & { configured: boolean };
      instagram: Partial<PlatformState> & { configured: boolean };
      storage: { ready: boolean; publicBaseUrl: string | null };
    }>('/social/platform-status'),
  });

/**
 * Die Developer-Apps der Installation. Nur der Betreiber darf sie sehen —
 * der Server antwortet allen anderen mit 403, deshalb wird die Abfrage für
 * sie gar nicht erst gestartet.
 */
export const usePlatformApps = (enabled = true) =>
  useQuery({
    queryKey: keys.platformApps,
    queryFn: () => api.get<{
      apps: PlatformState[];
      redirectUris: { tiktok: string; instagram: string };
      publicApiUrl: string;
    }>('/social/platform-apps'),
    enabled,
    retry: false,
  });

/* ------------------------------------------------------------ Mutationen */

export interface CustomerRow {
  orgId: string; name: string; ownerEmail: string | null; ownerName: string | null;
  plan: string; subscriptionStatus: string | null; hasPaymentMethod: boolean;
  creditBalance: number; creditsPurchased: number; creditsGifted: number; creditsSpent: number;
  payments: number; lastPaymentAt: string | null;
  revenueEur: number; costEur: number; contributionEur: number; createdAt: string | null;
  posts: number; publishedPosts: number; views: number;
  creditsPerPost: number; costEurPerPost: number;
}

/**
 * Kundenübersicht des Betreibers. Für normale Kunden antwortet der Server
 * mit 403 — deshalb wird hier nicht erneut versucht, sonst liefe die
 * Abfrage bei jedem Kunden dauerhaft ins Leere.
 */
export const useCustomers = (enabled = true) =>
  useQuery({
    queryKey: keys.customers,
    queryFn: () => api.get<{
      customers: CustomerRow[];
      totals: { customers: number; paying: number; revenueEur: number; costEur: number; contributionEur: number };
    }>('/billing/customers'),
    enabled,
    retry: false,
  });

export interface AgentStats {
  posts: number; published: number; credits: number;
  views: number; likes: number;
  creditsPerPost: number; creditsPer1kViews: number | null;
}

export interface AgentView {
  id: string; projectId: string; name: string; note: string;
  /** Erkennungsfarbe aus AGENT_COLORS, leer = noch keine vergeben */
  accentColor: string;
  videoShare: number;
  /** Was für ein Beitrag (Bildserie, Story-Reel, Clip, AI wählt) */
  postFormat: 'auto' | 'carousel' | 'story_reel' | 'clip';
  /** Erzählarten, leer = AI wählt */
  storyFormats: string[];
  /** Posts je Lauf von Hand. Der Autopilot baut je Platz genau einen. */
  postsPerRun: number;
  scenesPerStory: number; scenesMax: number; scenesAuto: boolean;
  textStyle: string;
  hashtagCount: number; hashtagMax: number; hashtagAuto: boolean;
  imageStyle: string; customStylePrompt: string;
  /** Leer = wie im Projekt */
  goals: string[]; tonality: string; ctaText: string;
  imageModel: string | null; videoModel: string | null; planningModel: string | null;
  musicMode: 'auto' | 'fixed' | 'none'; fixedTrackId: string | null;
  appShare: number; extraInstructions: string;
  /** Serien: auto = die AI entscheidet je Lauf, always = immer, never = nie */
  seriesMode?: 'auto' | 'always' | 'never';
  lastRunAt: string | null; createdAt: string | null;
  /** Geschaetzte Credits je Post, vor dem ersten Lauf */
  estimatePerPost: number;
  /** Was er tatsaechlich gebracht hat, null solange nichts lief */
  stats: AgentStats | null;
}

export const useAgents = (projectId: string | undefined) =>
  useQuery({
    queryKey: keys.agents(projectId ?? ''),
    queryFn: () => api.get<{ agents: AgentView[] }>(`/agents?projectId=${projectId}`).then((r) => r.agents),
    enabled: !!projectId,
  });

/** Ein einzelner Agent, aus der Liste des Projekts gefiltert. */
export const useAgent = (projectId: string | undefined, agentId: string | undefined) => {
  const q = useAgents(projectId);
  return { ...q, data: q.data?.find((a) => a.id === agentId) ?? null };
};

export interface WeekPlanSlot {
  at: string; agentId: string; agentName: string;
  videoShare: number; imageStyle: string;
}

export interface WeekPlanView {
  maxPerDay: number;
  maxPerWeek: number;
  perWeek: number;
  days: { date: string; slots: WeekPlanSlot[] }[];
}

/**
 * Der Wochenplan, wie der Taktgeber ihn abarbeitet.
 *
 * `draft` rechnet einen noch nicht gespeicherten Fahrplan durch, damit man
 * beim Verstellen sofort sieht, was sich aendert, statt erst nach dem
 * Speichern.
 */
export const useWeekPlan = (
  projectId: string | undefined,
  draft?: { agentId: string; perWeek: number }[],
) => {
  const q = draft?.length ? draft.map((e) => `${e.agentId}:${e.perWeek}`).join(',') : '';
  return useQuery({
    queryKey: [...keys.project(projectId ?? ''), 'weekplan', q],
    queryFn: () => api.get<WeekPlanView>(
      `/projects/${projectId}/weekplan${q ? `?plan=${encodeURIComponent(q)}` : ''}`,
    ),
    enabled: !!projectId,
  });
};

export const useAgentPosts = (agentId: string | null | undefined) =>
  useQuery({
    queryKey: keys.agentPosts(agentId ?? ''),
    queryFn: () => api.get<{ posts: PostView[] }>(`/agents/${agentId}/posts`).then((r) => r.posts),
    enabled: !!agentId,
  });

/**
 * Welche Anmeldewege dieser Server anbietet.
 *
 * Der Google-Knopf taucht nur auf, wenn Client-ID und Secret hinterlegt
 * sind -- ein Knopf, der in eine Fehlermeldung führt, ist schlimmer als
 * keiner.
 */
export const useAuthProviders = () =>
  useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api.get<{ password: boolean; google: boolean }>('/auth/providers'),
    staleTime: 5 * 60_000,
    retry: false,
  });

export function useInvalidate() {
  const qc = useQueryClient();
  return {
    projects: () => qc.invalidateQueries({ queryKey: keys.projects }),
    project: (id: string) => qc.invalidateQueries({ queryKey: keys.project(id) }),
    posts: () => qc.invalidateQueries({ queryKey: ['posts'] }),
    post: (id: string) => qc.invalidateQueries({ queryKey: keys.post(id) }),
    approvals: () => qc.invalidateQueries({ queryKey: keys.approvals }),
    run: (projectId: string) => qc.invalidateQueries({ queryKey: keys.run(projectId) }),
    runDetail: () => qc.invalidateQueries({ queryKey: ['run-detail'] }),
    agents: (projectId: string) => qc.invalidateQueries({ queryKey: keys.agents(projectId) }),
    billing: () => qc.invalidateQueries({ queryKey: keys.billing }),
    /*
     * Mit dem Katalog zusammen. Der Katalog sagt, welcher Anbieter „aktiv"
     * ist und ob gebaut werden kann -- er hält fünf Minuten. Wurde nach dem
     * Speichern eines Keys nur die Connector-Liste erneuert, stand die
     * Kachel weiter auf „inaktiv" und die Seite auf „Noch nicht startklar",
     * obwohl der Key längst drin war.
     */
    connectors: () => {
      void qc.invalidateQueries({ queryKey: keys.catalog });
      return qc.invalidateQueries({ queryKey: keys.connectors });
    },
    accounts: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
    platform: () => { void qc.invalidateQueries({ queryKey: keys.platform }); return qc.invalidateQueries({ queryKey: keys.platformApps }); },
    all: () => qc.invalidateQueries(),
  };
}

export { useMutation, useQueryClient };

/**
 * „Neu bauen": zieht die fehlenden Bilder eines fehlgeschlagenen Posts nach.
 * Der Post steht danach auf „wird gebaut" und meldet sich per SSE zurück.
 */
export function rebuildPost(postId: string): Promise<{ post: PostView }> {
  return api.post<{ post: PostView }>(`/posts/${postId}/rebuild`);
}

/* ------------------------------------------ TikTok: Auskunft vor dem Posten */

export interface TiktokCreatorInfo {
  connected: boolean;
  canDirectPost: boolean;
  canPullFromUrl?: boolean;
  account?: { handle: string; displayName: string; avatarUrl: string | null };
  privacyOptions?: string[];
  commentDisabled?: boolean;
  duetDisabled?: boolean;
  stitchDisabled?: boolean;
  maxDurationSec?: number | null;
  reason?: string | null;
}

/**
 * Was das TikTok-Konto gerade erlaubt.
 *
 * Bewusst ohne Zwischenspeicherung: TikTok verlangt in den Richtlinien fuer
 * Direct Post, dass diese Auskunft unmittelbar vor dem Anzeigen der
 * Bestaetigungsseite frisch geholt wird. Ein Wert von vorhin koennte eine
 * Sichtbarkeit anbieten, die die API danach ablehnt -- der Beitrag stuende
 * dann auf "freigegeben" und ginge nie raus.
 */
export const useTiktokCreatorInfo = (projectId: string | null | undefined) =>
  useQuery({
    queryKey: keys.tiktokCreatorInfo(projectId ?? ''),
    queryFn: () => api.get<TiktokCreatorInfo>(`/social/tiktok/creator-info?projectId=${projectId}`),
    enabled: !!projectId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });

/** Ein Produkt im Katalog eines Produktprojekts (Affiliate oder eigenes). */
export interface CatalogProduct {
  id?: string;
  name: string;
  url: string;
  description: string;
  affiliate: boolean;
  disclosure: string;
  photoAssetIds: string[];
  active: boolean;
}
